import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFocusMode } from "@/context/FocusModeContext";
import { ASK_NAV, EXPLORE_GROUPS, PRIMARY_NAV } from "./navConfig";
import { gradeLevelStorage } from "@/lib/localStorage";

// Related destinations for finished browsing, never for a live study session.
const NEXT_STEPS: Record<string, [string, string][]> = {
  "/dashboard": [
    ["/curriculum", "Find a topic to learn"],
    ["/documents", "Bring your own study material"],
  ],
  "/curriculum": [
    ["/classroom", "Learn with a guided lesson"],
    ["/planner", "Make time for your subjects"],
  ],
  "/curriculum/workspace": [
    ["/documents", "Add material to your topics"],
    ["/planner", "Plan your next review"],
  ],
  "/progress": [
    ["/curriculum", "Explore your subjects & syllabus"],
    ["/planner", "Make time for your next review"],
  ],
  "/planner": [
    ["/study", "Turn your plan into practice"],
    ["/progress", "See how your practice adds up"],
  ],
  "/study": [
    ["/questions", "Try an exam paper or worksheet"],
    ["/groups", "Find support in a study group"],
  ],
  "/documents": [
    ["/summaries", "Turn your material into notes"],
    ["/study", "Choose how to study it"],
  ],
  "/summaries": [
    ["/flashcards", "Remember the key ideas"],
    ["/quiz-study", "Check what you understand"],
  ],
  "/flashcards": [
    ["/quiz-study", "Put your recall to the test"],
    ["/progress", "See what is getting stronger"],
  ],
  "/quiz-study": [
    ["/questions", "Practise with exam questions"],
    ["/progress", "Find your next area to review"],
  ],
  "/questions": [
    ["/classroom", "Work through a guided lesson"],
    ["/curriculum", "Find the topics behind a paper"],
  ],
  "/classroom": [
    ["/summaries", "Keep the ideas in your notes"],
    ["/groups", "Learn alongside your study group"],
  ],
  "/groups": [
    ["/study", "Keep your own learning moving"],
    ["/achievements", "Look back at your milestones"],
  ],
  "/achievements": [
    ["/dashboard", "Find your next step for today"],
    ["/progress", "See the learning behind your milestones"],
  ],
  "/settings": [
    ["/help", "Get to know your study tools"],
    ["/whats-new", "See what has changed"],
  ],
  "/whats-new": [
    ["/study", "Try the tools in your study routine"],
    ["/feedback", "Help shape what comes next"],
  ],
  "/feedback": [
    ["/whats-new", "Catch up on recent improvements"],
    ["/settings", "Make Novari feel like yours"],
  ],
  "/contact": [
    ["/help", "Find answers to common questions"],
    ["/settings", "Check your account preferences"],
  ],
};

const destinations = [
  ASK_NAV,
  ...PRIMARY_NAV,
  ...EXPLORE_GROUPS.flatMap((group) => group.items),
];

export default function NextSteps() {
  const { pathname, search } = useLocation();
  const { isFocusMode } = useFocusMode();
  const links = (NEXT_STEPS[pathname] || []).flatMap(([path, reason]) => {
    const item = destinations.find((item) => item.path === path);
    return item && (!isFocusMode || item.essential)
      ? [{ ...item, reason }]
      : [];
  });
  if (!links.length || gradeLevelStorage.isKids() || (pathname === "/study" && new URLSearchParams(search).has("topic"))) return null;

  return (
    <nav aria-label="Keep exploring" className="mt-8 border-t border-line pt-5">
      <p className="mb-3 text-xs font-semibold text-ink-2">Where to next?</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {links.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="group flex min-h-16 items-center gap-3 rounded-2xl p-3 outline-none transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ember"
          >
            <item.icon
              className="h-5 w-5 shrink-0 text-ember"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">
                {item.reason}
              </span>
              <span className="mt-1 block text-xs text-ink-2">{item.name}</span>
            </span>
            <ArrowUpRight
              className="h-4 w-4 shrink-0 text-ink-3 group-hover:text-ember"
              aria-hidden="true"
            />
          </Link>
        ))}
      </div>
    </nav>
  );
}
